import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import { Scale, ArrowRight, Gift } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "associate" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register(form);
      toast.success(lang === "el" ? "Λογαριασμός δημιουργήθηκε" : "Account created");
      navigate("/onboarding");
    } catch (er) {
      const msg = er?.response?.data?.detail || (lang === "el" ? "Αποτυχία εγγραφής" : "Failed");
      setErr(typeof msg === "string" ? msg : "Σφάλμα");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="size-11 rounded-lg flex items-center justify-center bg-[hsl(var(--gold)/0.15)] border gold-border">
            <Scale className="size-6 gold-text" strokeWidth={1.6}/>
          </div>
          <div>
            <div className="font-heading font-bold text-base leading-none">ΙΧΝΟΔΙΚΗΣ</div>
            <div className="font-mono text-[10px] gold-text uppercase tracking-[0.25em] mt-1.5">{t("create_account")}</div>
          </div>
        </div>
        <h2 className="font-display text-3xl font-semibold mb-2">{t("create_account")}</h2>
        {refCode && (
          <div className="flex items-center gap-2 mb-6 p-3 rounded border border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.08)]" data-testid="referral-banner">
            <Gift className="size-4 gold-text"/>
            <span className="text-[12.5px]">Σας προσκάλεσε φίλος! Κωδικός: <code className="gold-text font-mono">{refCode}</code></span>
          </div>
        )}
        {!refCode && <div className="mb-6"/>}
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          {["name", "email", "password"].map(k => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{t(k)}</label>
              <input
                data-testid={`register-${k}-input`}
                type={k === "password" ? "password" : k === "email" ? "email" : "text"}
                required
                value={form[k]} onChange={e => setForm({...form, [k]: e.target.value})}
                className="w-full mt-1.5 bg-muted/30 border border-border/80 rounded-md px-3 py-2.5 text-[13.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
              />
            </div>
          ))}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{t("role")}</label>
            <select
              data-testid="register-role-select"
              value={form.role} onChange={e => setForm({...form, role: e.target.value})}
              className="w-full mt-1.5 bg-muted/30 border border-border/80 rounded-md px-3 py-2.5 text-[13.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
            >
              <option value="associate">Associate (Συνεργάτης)</option>
              <option value="paralegal">Paralegal (Νομικός βοηθός)</option>
              <option value="admin">Admin (Διαχειριστής)</option>
              <option value="viewer">Viewer (Read-only)</option>
            </select>
          </div>
          {err && <div className="text-red-400 text-[12px] font-mono">{err}</div>}
          <button
            data-testid="register-submit"
            type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13.5px] hover:opacity-90 disabled:opacity-60 transition-all"
          >
            {loading ? t("loading") : t("create_account")} <ArrowRight className="size-4"/>
          </button>
        </form>
        <div className="mt-6 text-[12.5px] text-muted-foreground">
          {t("has_account")}{" "}
          <Link to="/login" className="gold-text hover:underline" data-testid="register-to-login">{t("login")}</Link>
        </div>
      </div>
    </div>
  );
}
